import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { CheckCircle, Phone, Sun, Thermometer, AlertTriangle, FileCheck, Shield } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

const PartOOverheating = () => {
  const faqs = [
    {
      question: "What is Part O?",
      answer: "Part O is the section of the Building Regulations (England) addressing overheating risk in new residential buildings. Introduced in June 2022, it requires new dwellings to be designed to limit unwanted solar gains and provide adequate ventilation to remove excess heat, protecting occupants from the health risks of overheating."
    },
    {
      question: "When does Part O apply?",
      answer: "Part O applies to all new dwellings in England, including houses, flats, conversions to dwellings, and residential buildings. It came into force on 15 June 2022 for projects with Building Regulations applications submitted from that date. It does not apply to existing buildings unless they're being converted to dwellings."
    },
    {
      question: "What are the compliance methods?",
      answer: "Part O offers two compliance routes: the Simplified Method (prescriptive approach using glazing limits, shading and cross-ventilation rules) or Dynamic Thermal Modelling (detailed simulation demonstrating hours of overheating below threshold). Most projects use the simplified method; complex designs may need modelling."
    },
    {
      question: "What factors affect overheating risk?",
      answer: "Key factors include: location (London is highest risk), orientation (south and west facing worst), glazing ratio and g-value, external shading, thermal mass, ventilation strategy (cross-ventilation, purge ventilation), and whether the dwelling is a flat or house. All these are assessed in the Part O analysis."
    },
    {
      question: "What's the difference between Part O and TM59?",
      answer: "Part O is a Building Regulations requirement focusing on limiting overheating risk in new dwellings using simplified or modelling approaches. TM59 (CIBSE) is a more detailed overheating assessment methodology often required by planning authorities, particularly in London. We can provide both assessments."
    }
  ];

  return (
    <>
      <Helmet>
        <title>Part O Overheating Assessment | Building Regulations | Overheating Risk | Energy Performance Direct</title>
        <meta name="description" content="Professional Part O overheating assessments for new dwellings. Building Regulations compliance for residential overheating risk. Simplified and modelling approaches. Call 01392 980077." />
        <meta name="keywords" content="Part O assessment, overheating risk, building regulations overheating, TM59, residential overheating, new build overheating, simplified method overheating" />
        <link rel="canonical" href="https://energyperformancedirect.co.uk/part-o-overheating" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
        <Navbar />

        <section className="py-16 bg-gradient-to-br from-red-50 via-orange-50 to-yellow-50">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-block mb-4 px-4 py-2 bg-white rounded-full shadow-sm">
                <span className="text-sm font-medium text-red-600">Building Regulations Part O</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Part O Overheating Assessment
              </h1>
              <p className="text-xl text-gray-600 mb-8">
                Professional overheating risk assessments for new dwellings. Demonstrate Building Regulations Part O compliance using simplified or dynamic modelling methods.
              </p>
              <div className="flex flex-wrap justify-center gap-4 mb-8">
                <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-lg shadow-sm">
                  <CheckCircle className="h-5 w-5 text-red-600" />
                  <span className="text-sm font-medium">From £150</span>
                </div>
                <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-lg shadow-sm">
                  <CheckCircle className="h-5 w-5 text-red-600" />
                  <span className="text-sm font-medium">Part O Compliant</span>
                </div>
                <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-lg shadow-sm">
                  <CheckCircle className="h-5 w-5 text-red-600" />
                  <span className="text-sm font-medium">Design Support</span>
                </div>
              </div>
              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/booking">
                  <Button size="lg" className="bg-red-600 hover:bg-red-700">
                    Request Part O Quote
                  </Button>
                </Link>
                <a href="tel:01392980077">
                  <Button size="lg" variant="outline" className="border-red-600 text-red-600 hover:bg-red-50">
                    <Phone className="mr-2 h-5 w-5" />
                    01392 980077
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-12">Part O Assessment Services</h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                <Card className="hover:shadow-lg transition-shadow">
                  <CardContent className="pt-6">
                    <FileCheck className="h-12 w-12 text-red-600 mb-4" />
                    <h3 className="text-xl font-semibold mb-2">Simplified Method</h3>
                    <p className="text-gray-600">
                      Prescriptive compliance using glazing limits, shading requirements and ventilation provisions. Suitable for most standard dwelling designs.
                    </p>
                  </CardContent>
                </Card>
                <Card className="hover:shadow-lg transition-shadow">
                  <CardContent className="pt-6">
                    <Thermometer className="h-12 w-12 text-red-600 mb-4" />
                    <h3 className="text-xl font-semibold mb-2">Dynamic Thermal Modelling</h3>
                    <p className="text-gray-600">
                      Detailed hour-by-hour simulation for complex designs. Demonstrates compliance when simplified method limits are exceeded.
                    </p>
                  </CardContent>
                </Card>
                <Card className="hover:shadow-lg transition-shadow">
                  <CardContent className="pt-6">
                    <Sun className="h-12 w-12 text-red-600 mb-4" />
                    <h3 className="text-xl font-semibold mb-2">TM59 Assessments</h3>
                    <p className="text-gray-600">
                      CIBSE TM59 overheating assessments for planning requirements, particularly London developments and local authority conditions.
                    </p>
                  </CardContent>
                </Card>
                <Card className="hover:shadow-lg transition-shadow">
                  <CardContent className="pt-6">
                    <Shield className="h-12 w-12 text-red-600 mb-4" />
                    <h3 className="text-xl font-semibold mb-2">Design Stage Support</h3>
                    <p className="text-gray-600">
                      Early-stage advice on glazing ratios, orientation, shading strategies and ventilation to avoid overheating issues before plans are finalised.
                    </p>
                  </CardContent>
                </Card>
                <Card className="hover:shadow-lg transition-shadow">
                  <CardContent className="pt-6">
                    <AlertTriangle className="h-12 w-12 text-red-600 mb-4" />
                    <h3 className="text-xl font-semibold mb-2">Noise-Sensitive Sites</h3>
                    <p className="text-gray-600">
                      Special provisions for dwellings near roads, railways and airports where windows cannot be opened. Alternative ventilation strategies assessed.
                    </p>
                  </CardContent>
                </Card>
                <Card className="hover:shadow-lg transition-shadow">
                  <CardContent className="pt-6">
                    <FileCheck className="h-12 w-12 text-red-600 mb-4" />
                    <h3 className="text-xl font-semibold mb-2">Compliance Reports</h3>
                    <p className="text-gray-600">
                      Comprehensive reports for Building Control demonstrating Part O compliance, including all calculations and design recommendations.
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-8">Part O Risk Factors</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <Card className="bg-red-50 border-red-200">
                  <CardContent className="pt-6">
                    <h3 className="font-semibold text-lg mb-4 text-red-800">Higher Risk</h3>
                    <ul className="space-y-2 text-gray-700">
                      <li className="flex items-start gap-2">
                        <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                        <span>Location in London or South East</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                        <span>South or West facing bedrooms</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                        <span>Single aspect flats</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                        <span>Large glazing areas</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                        <span>No external shading</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                        <span>Limited ventilation openings</span>
                      </li>
                    </ul>
                  </CardContent>
                </Card>
                <Card className="bg-green-50 border-green-200">
                  <CardContent className="pt-6">
                    <h3 className="font-semibold text-lg mb-4 text-green-800">Mitigation Strategies</h3>
                    <ul className="space-y-2 text-gray-700">
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <span>External shading (overhangs, brise soleil)</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <span>Reduced glazing ratios</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <span>Low g-value glazing</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <span>Cross-ventilation design</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <span>Purge ventilation provision</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <span>Thermal mass utilisation</span>
                      </li>
                    </ul>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-8">Part O Overheating FAQs</h2>
              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <Card key={index}>
                    <CardContent className="pt-6">
                      <h3 className="font-semibold text-lg mb-2">{faq.question}</h3>
                      <p className="text-gray-600">{faq.answer}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 bg-red-600 text-white">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold mb-4">Need a Part O Assessment?</h2>
            <p className="text-xl mb-8 opacity-90">
              Professional overheating risk assessments for Building Regulations compliance
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/booking">
                <Button size="lg" variant="secondary" className="bg-white text-red-600 hover:bg-gray-100">
                  Request Quote
                </Button>
              </Link>
              <a href="tel:01392980077">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-red-700">
                  <Phone className="mr-2 h-5 w-5" />
                  01392 980077
                </Button>
              </a>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default PartOOverheating;
